/**
 * NotificationCenter Component
 * 통합 알림 센터 - 중요 알림 모아보기
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BellAlertIcon,
  CreditCardIcon,
  UserGroupIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  ArrowRightIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { API_BASE_URL } from '../../../config/api';

interface CreditInfo {
  balance: number;
  free_quota: number;
  free_used: number;
  free_remaining: number;
}

interface ExpiringMember {
  id: string;
  name: string;
  phone: string;
  expiry_date: string;
  days_left: number;
}

interface AlertItem {
  id: string;
  type: 'critical' | 'warning' | 'info' | 'success';
  category: 'system' | 'member' | 'payment';
  title: string;
  message: string;
  link?: string;
  linkText?: string;
  timestamp?: string;
}

const NotificationCenter: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const [dismissedAlerts, setDismissedAlerts] = useState<string[]>([]);

  useEffect(() => {
    fetchAllAlerts();
  }, []);

  const fetchAllAlerts = async () => {
    setLoading(true);
    const token = localStorage.getItem('accessToken') || localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    const newAlerts: AlertItem[] = [];

    try {
      // 1. 크레딧 정보 조회
      try {
        const creditRes = await fetch(`${API_BASE_URL}/api/v1/owner/credits/balance`, { headers });
        if (creditRes.ok) {
          const creditData = await creditRes.json();
          if (creditData.success && creditData.credit) {
            const credit: CreditInfo = creditData.credit;

            if (credit.balance <= 3000) {
              newAlerts.push({
                id: 'credit-critical',
                type: 'critical',
                category: 'system',
                title: '크레딧 긴급 부족',
                message: `현재 잔액 ${credit.balance.toLocaleString()}원 - 알림톡 발송이 곧 중단됩니다!`,
                link: '/owner/usage',
                linkText: '충전하기',
              });
            } else if (credit.balance <= 10000) {
              newAlerts.push({
                id: 'credit-warning',
                type: 'warning',
                category: 'system',
                title: '크레딧 잔액 부족',
                message: `현재 잔액 ${credit.balance.toLocaleString()}원 - 충전이 필요합니다.`,
                link: '/owner/usage',
                linkText: '충전하기',
              });
            } else {
              newAlerts.push({
                id: 'credit-ok',
                type: 'success',
                category: 'system',
                title: '크레딧 정상',
                message: `현재 잔액 ${credit.balance.toLocaleString()}원`,
                link: '/owner/usage',
                linkText: '상세보기',
              });
            }
          }
        }
      } catch (e) {
        console.error('크레딧 조회 실패:', e);
      }

      // 2. 만료 임박 회원 조회
      try {
        const expiryRes = await fetch(`${API_BASE_URL}/api/v1/owner/members/expiring?days=7`, { headers });
        if (expiryRes.ok) {
          const expiryData = await expiryRes.json();
          const members = expiryData.members || expiryData.data || [];

          if (members.length > 0) {
            const criticalCount = members.filter((m: ExpiringMember) => m.days_left <= 1).length;
            const warningCount = members.filter((m: ExpiringMember) => m.days_left > 1 && m.days_left <= 3).length;

            if (criticalCount > 0) {
              newAlerts.push({
                id: 'expiry-critical',
                type: 'critical',
                category: 'member',
                title: '오늘/내일 만료 회원',
                message: `${criticalCount}명의 이용권이 오늘 또는 내일 만료됩니다.`,
                link: '/owner/members',
                linkText: '회원 관리',
              });
            }

            if (warningCount > 0) {
              newAlerts.push({
                id: 'expiry-warning',
                type: 'warning',
                category: 'member',
                title: '만료 임박 회원',
                message: `${warningCount}명의 이용권이 3일 이내 만료됩니다.`,
                link: '/owner/members',
                linkText: '회원 관리',
              });
            }

            if (members.length > criticalCount + warningCount) {
              const infoCount = members.length - criticalCount - warningCount;
              newAlerts.push({
                id: 'expiry-info',
                type: 'info',
                category: 'member',
                title: '7일 내 만료 예정',
                message: `${infoCount}명의 이용권이 7일 이내 만료됩니다.`,
                link: '/owner/members',
                linkText: '회원 관리',
              });
            }
          } else {
            newAlerts.push({
              id: 'expiry-ok',
              type: 'success',
              category: 'member',
              title: '만료 임박 회원 없음',
              message: '7일 이내 만료 예정인 회원이 없습니다.',
            });
          }
        }
      } catch (e) {
        console.error('만료 회원 조회 실패:', e);
      }

      // 3. 시스템 공지 (하드코딩 예시 - 나중에 API로 대체)
      newAlerts.push({
        id: 'system-notice-1',
        type: 'info',
        category: 'system',
        title: '시스템 안내',
        message: '알림톡 발송 시 크레딧이 차감됩니다. 잔액을 확인해주세요.',
        timestamp: new Date().toISOString(),
      });

    } catch (error) {
      console.error('알림 조회 오류:', error);
    } finally {
      setAlerts(newAlerts);
      setLoading(false);
    }
  };

  const dismissAlert = (alertId: string) => {
    setDismissedAlerts([...dismissedAlerts, alertId]);
  };

  const getAlertStyle = (type: AlertItem['type']) => {
    switch (type) {
      case 'critical':
        return 'bg-red-50 border-red-300 text-red-800';
      case 'warning':
        return 'bg-yellow-50 border-yellow-300 text-yellow-800';
      case 'success':
        return 'bg-green-50 border-green-300 text-green-800';
      default:
        return 'bg-blue-50 border-blue-300 text-blue-800';
    }
  };

  const getAlertIcon = (type: AlertItem['type']) => {
    switch (type) {
      case 'critical':
        return <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />;
      case 'warning':
        return <ExclamationTriangleIcon className="h-6 w-6 text-yellow-600" />;
      case 'success':
        return <CheckCircleIcon className="h-6 w-6 text-green-600" />;
      default:
        return <BellAlertIcon className="h-6 w-6 text-blue-600" />;
    }
  };

  const getCategoryIcon = (category: AlertItem['category']) => {
    switch (category) {
      case 'system':
        return <BellAlertIcon className="h-5 w-5" />;
      case 'member':
        return <UserGroupIcon className="h-5 w-5" />;
      case 'payment':
        return <CreditCardIcon className="h-5 w-5" />;
    }
  };

  const getCategoryLabel = (category: AlertItem['category']) => {
    switch (category) {
      case 'system':
        return '시스템';
      case 'member':
        return '회원';
      case 'payment':
        return '결제';
    }
  };

  const visibleAlerts = alerts.filter(a => !dismissedAlerts.includes(a.id));
  const criticalAlerts = visibleAlerts.filter(a => a.type === 'critical');
  const warningAlerts = visibleAlerts.filter(a => a.type === 'warning');
  const otherAlerts = visibleAlerts.filter(a => a.type !== 'critical' && a.type !== 'warning');

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">알림을 불러오는 중...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      {/* 헤더 */}
      <div className="mb-6">
        <div className="flex items-center space-x-3">
          <BellAlertIcon className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">알림 센터</h1>
            <p className="text-sm text-gray-600">중요한 알림을 한눈에 확인하세요</p>
          </div>
        </div>
      </div>

      {/* 요약 카드 */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className={`rounded-lg p-4 border-2 ${criticalAlerts.length > 0 ? 'bg-red-50 border-red-300' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">긴급 알림</p>
              <p className={`text-2xl font-bold ${criticalAlerts.length > 0 ? 'text-red-600' : 'text-gray-400'}`}>
                {criticalAlerts.length}
              </p>
            </div>
            <ExclamationTriangleIcon className={`h-10 w-10 ${criticalAlerts.length > 0 ? 'text-red-400' : 'text-gray-300'}`} />
          </div>
        </div>

        <div className={`rounded-lg p-4 border-2 ${warningAlerts.length > 0 ? 'bg-yellow-50 border-yellow-300' : 'bg-gray-50 border-gray-200'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">주의 알림</p>
              <p className={`text-2xl font-bold ${warningAlerts.length > 0 ? 'text-yellow-600' : 'text-gray-400'}`}>
                {warningAlerts.length}
              </p>
            </div>
            <ClockIcon className={`h-10 w-10 ${warningAlerts.length > 0 ? 'text-yellow-400' : 'text-gray-300'}`} />
          </div>
        </div>

        <div className="rounded-lg p-4 border-2 bg-blue-50 border-blue-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">전체 알림</p>
              <p className="text-2xl font-bold text-blue-600">{visibleAlerts.length}</p>
            </div>
            <BellAlertIcon className="h-10 w-10 text-blue-400" />
          </div>
        </div>
      </div>

      {/* 긴급 알림 */}
      {criticalAlerts.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-red-700 mb-3 flex items-center">
            <ExclamationTriangleIcon className="h-5 w-5 mr-2" />
            긴급 알림
          </h2>
          <div className="space-y-3">
            {criticalAlerts.map(alert => (
              <div key={alert.id} className={`rounded-lg border-2 p-4 ${getAlertStyle(alert.type)}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    {getAlertIcon(alert.type)}
                    <div>
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-xs font-medium px-2 py-0.5 bg-white/50 rounded">
                          {getCategoryLabel(alert.category)}
                        </span>
                        <h3 className="font-semibold">{alert.title}</h3>
                      </div>
                      <p className="text-sm">{alert.message}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {alert.link && (
                      <button
                        onClick={() => navigate(alert.link!)}
                        className="flex items-center px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700"
                      >
                        {alert.linkText}
                        <ArrowRightIcon className="h-4 w-4 ml-1" />
                      </button>
                    )}
                    <button
                      onClick={() => dismissAlert(alert.id)}
                      className="p-1 hover:bg-red-200 rounded"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 주의 알림 */}
      {warningAlerts.length > 0 && (
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-yellow-700 mb-3 flex items-center">
            <ClockIcon className="h-5 w-5 mr-2" />
            주의 필요
          </h2>
          <div className="space-y-3">
            {warningAlerts.map(alert => (
              <div key={alert.id} className={`rounded-lg border-2 p-4 ${getAlertStyle(alert.type)}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    {getAlertIcon(alert.type)}
                    <div>
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-xs font-medium px-2 py-0.5 bg-white/50 rounded">
                          {getCategoryLabel(alert.category)}
                        </span>
                        <h3 className="font-semibold">{alert.title}</h3>
                      </div>
                      <p className="text-sm">{alert.message}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {alert.link && (
                      <button
                        onClick={() => navigate(alert.link!)}
                        className="flex items-center px-3 py-1.5 bg-yellow-600 text-white text-sm rounded-lg hover:bg-yellow-700"
                      >
                        {alert.linkText}
                        <ArrowRightIcon className="h-4 w-4 ml-1" />
                      </button>
                    )}
                    <button
                      onClick={() => dismissAlert(alert.id)}
                      className="p-1 hover:bg-yellow-200 rounded"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 기타 알림 */}
      {otherAlerts.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-700 mb-3 flex items-center">
            <BellAlertIcon className="h-5 w-5 mr-2" />
            일반 알림
          </h2>
          <div className="space-y-3">
            {otherAlerts.map(alert => (
              <div key={alert.id} className={`rounded-lg border-2 p-4 ${getAlertStyle(alert.type)}`}>
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    {getAlertIcon(alert.type)}
                    <div>
                      <div className="flex items-center space-x-2 mb-1">
                        <span className="text-xs font-medium px-2 py-0.5 bg-white/50 rounded">
                          {getCategoryLabel(alert.category)}
                        </span>
                        <h3 className="font-semibold">{alert.title}</h3>
                      </div>
                      <p className="text-sm">{alert.message}</p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    {alert.link && (
                      <button
                        onClick={() => navigate(alert.link!)}
                        className="flex items-center px-3 py-1.5 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700"
                      >
                        {alert.linkText}
                        <ArrowRightIcon className="h-4 w-4 ml-1" />
                      </button>
                    )}
                    <button
                      onClick={() => dismissAlert(alert.id)}
                      className="p-1 hover:bg-gray-200 rounded"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 알림 없음 */}
      {visibleAlerts.length === 0 && (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <CheckCircleIcon className="h-16 w-16 text-green-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">모든 알림을 확인했습니다</h3>
          <p className="text-gray-600">새로운 알림이 있으면 여기에 표시됩니다.</p>
        </div>
      )}

      {/* 새로고침 버튼 */}
      <div className="mt-6 text-center">
        <button
          onClick={fetchAllAlerts}
          className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
        >
          새로고침
        </button>
      </div>
    </div>
  );
};

export default NotificationCenter;
